// src/redux/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchWrapper } from "../utils/fetchWrapper";

// Load user from localStorage if available
const savedUser = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user"))
  : null;

const savedToken = localStorage.getItem("token") || null;

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password, clientInfo }, { rejectWithValue }) => {
    try {
      const res = await fetchWrapper.post(
        "/login",
        { email, password, clientInfo },
        { withCredentials: true },
      );

      if (res.statusText !== "OK") {
        const errorData = await res.data;
        return rejectWithValue(errorData.message || "Login failed");
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Logout - calls backend to clear httpOnly cookie
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await fetchWrapper.post("/logout", {}, { withCredentials: true });
      return true;
    } catch (err) {
      // Even if API fails, we should still clear local state
      console.error("Logout API error:", err);
      return true;
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: savedUser,
    token: savedToken,
    permissions: localStorage.getItem("permissions")
      ? JSON.parse(localStorage.getItem("permissions"))
      : {},
    isInstalled: localStorage.getItem("isInstalled") === "true",
    loading: false,
    error: null,
    loginAttempts: 0,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.permissions = {};
      state.loginAttempts = 0;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("permissions");
      localStorage.removeItem("isInstalled");
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload;
      localStorage.setItem("permissions", JSON.stringify(action.payload));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.permissions = action.payload.user.permissions || {};
        state.isInstalled = action.payload.isInstalled;
        state.loginAttempts = 0;
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        localStorage.setItem("token", action.payload.token);
        localStorage.setItem(
          "permissions",
          JSON.stringify(action.payload.user.permissions || {}),
        );
        localStorage.setItem("isInstalled", action.payload.isInstalled);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.loginAttempts += 1;
      })
      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.permissions = {};
        state.loginAttempts = 0;
        localStorage.removeItem("user");
        localStorage.removeItem("permissions");
      })
      .addCase(logoutUser.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.permissions = {};
        state.loginAttempts = 0;
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("permissions");
      });
  },
});

export const { logout, setPermissions } = authSlice.actions;
export default authSlice.reducer;
